"use client";

import { formatTimeRangeWithPreferences } from "@workspace/shared-utils";
import { Card, CardContent, CardHeader } from "@workspace/ui/components/card";
import { Label } from "@workspace/ui/components/label";
import { cn } from "@workspace/ui/lib/utils";
import { memo, useMemo } from "react";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ClickableArtistPhoto } from "@/components/shared/photos/clickable-artist-photo";
import { SocialButtonGroup } from "@/components/shared/social-button-group";
import { SpotifyPreviewPill } from "@/components/shared/spotify-preview-pill";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import type { ShowArtist } from "@/lib/types/show-types";

interface ShowCollaboratorCardProps {
	showArtist: ShowArtist;
	className?: string;
}

export const ShowCollaboratorCard = memo<ShowCollaboratorCardProps>(
	function ShowCollaboratorCard({ showArtist, className }) {
		const preferences = useUserPreferences();
		const { artist, time_start, time_end } = showArtist;

		const timeRange = useMemo(() => {
			if (!time_start) return null;

			try {
				return formatTimeRangeWithPreferences(
					time_start,
					time_end || undefined,
					preferences,
				);
			} catch {
				return null;
			}
		}, [time_start, time_end, preferences]);

		if (!artist) {
			return null;
		}

		const hasGenres = Boolean(
			(Array.isArray(artist.spotify_genres) && artist.spotify_genres.length > 0) ||
			(Array.isArray(artist.manual_genres) && artist.manual_genres.length > 0)
		);
		const hasPreview = Boolean(artist.spotify_id);

		return (
			<Card className={cn("w-full border bg-card", className)}>
				<CardHeader className="p-3">
					<div className="flex items-start justify-between">
						<div className="flex min-w-0 flex-1 items-start gap-3">
							<ClickableArtistPhoto
								href={`/artists/${createArtistSlug(artist.name, artist.slug)}`}
								name={artist.name}
								imageUrl={artist.image_url}
								size="md"
								rounded="card"
								trackingData={{
									artistId: artist.id,
									artistName: artist.name,
									source: "discover",
								}}
							/>

							<div className="min-w-0 flex-1 flex flex-col gap-1">
								<h4 className="truncate font-semibold text-sm leading-tight">
									{artist.name}
								</h4>

								{timeRange && (
									<div className="text-muted-foreground text-xs">
										{timeRange}
									</div>
								)}

								<SocialButtonGroup
									name={artist.name}
									links={{
										spotify: artist.spotify_url || null,
										youtube: artist.youtube_url || null,
										instagram: artist.instagram_url || null,
										twitter: artist.twitter_url || null,
										website: artist.website_url || null,
									}}
									size="xs"
									maxVisible={3}
								/>
							</div>
						</div>
					</div>
				</CardHeader>

				{(hasGenres || hasPreview) && (
					<CardContent className="px-3 pt-0 pb-3">
						{/* Aligned with artist info (after photo) */}
						<div className="flex gap-3">
							{/* Spacer to align with photo width (48px = w-12) */}
							<div className="w-12 flex-shrink-0" />
							<div className={cn("flex-1", hasGenres && hasPreview ? "grid grid-cols-2 gap-4" : "")}>
								{hasGenres && (
									<div className="space-y-1">
										<Label>Genres</Label>
										<GenreBadgeListFromData
											spotifyGenres={artist.spotify_genres || null}
											manualGenres={artist.manual_genres || null}
											maxDisplay={3}
											badgeClassName="h-5 px-2 text-xs"
										/>
									</div>
								)}

								{hasPreview && (
									<div className="space-y-1">
										<Label>Preview</Label>
										<SpotifyPreviewPill
											spotifyId={artist.spotify_id!}
											artistName={artist.name}
										/>
									</div>
								)}
							</div>
						</div>
					</CardContent>
				)}
			</Card>
		);
	},
);
